/**
 * Minimum tier per admin route — derived from the canonical role registry
 * (backend/core/role_registry.py) and applied centrally by `RequireAuth`, so
 * every /admin/* route is gated without touching 141 route elements.
 *
 * Longest prefix wins. The backend still enforces each endpoint; this exists
 * so a user sees "you need X" instead of a screen full of failed requests.
 */
export const ADMIN_ROUTE_TIERS = [
  // platform-wide money, identity and infrastructure
  ['/admin/revenue', 'platform_admin'],
  ['/admin/billing', 'platform_admin'],
  ['/admin/platform', 'platform_admin'],
  ['/admin/rbac', 'platform_admin'],
  ['/admin/scopes', 'platform_admin'],
  ['/admin/roles', 'platform_admin'],
  ['/admin/admins', 'platform_admin'],
  ['/admin/control-centre', 'platform_admin'],
  ['/admin/integrations', 'platform_admin'],
  ['/admin/settings', 'org_admin'],

  // money and governance
  ['/admin/bank', 'manager'],
  ['/admin/finance', 'manager'],
  // custodians are appointed independently of admin tier; the backend decides
  ['/admin/finance/access', 'viewer'],
  ['/admin/approvals', 'manager'],
  ['/admin/giving', 'manager'],
  ['/admin/donations', 'manager'],
  ['/admin/payments', 'manager'],
  ['/admin/expenses', 'manager'],

  // people and sensitive records
  ['/admin/hospital', 'org_admin'],
  ['/admin/hospitals', 'org_admin'],
  ['/admin/members', 'manager'],
  ['/admin/clergy', 'manager'],
  ['/admin/safeguarding', 'org_admin'],
  ['/admin/hr', 'org_admin'],

  // security estate
  ['/admin/cctv', 'manager'],
  ['/admin/security', 'manager'],
  ['/admin/gate', 'operator'],
  ['/admin/patrols', 'operator'],

  // organisation structure
  ['/admin/churches', 'manager'],
  ['/admin/provinces', 'org_admin'],
  ['/admin/hierarchy', 'org_admin'],
  ['/admin/schools', 'manager'],
  ['/admin/campuses', 'manager'],

  // day-to-day operations — most staff live here
  ['/admin/events', 'operator'],
  ['/admin/delegates', 'operator'],
  ['/admin/checkin', 'operator'],
  ['/admin/dining', 'operator'],
  ['/admin/accommodation', 'operator'],
  ['/admin/housing', 'operator'],
  ['/admin/pos', 'operator'],
  ['/admin/facilities', 'operator'],
  ['/admin/media', 'operator'],
  ['/admin/fwtv', 'operator'],
  ['/admin/studio', 'operator'],
  ['/admin/bookshop', 'operator'],
  ['/admin/campaigns', 'operator'],
  ['/admin/comms', 'operator'],
  ['/admin/reports', 'viewer'],
  ['/admin/analytics', 'viewer'],
];

/** Minimum tier for a path, or null when the route has no tier requirement. */
export const requiredTierFor = (pathname = '') => {
  const path = (pathname || '').toLowerCase();
  // One church's workspace is open to its office holders (Elder, Deacon,
  // Ministry Leader — operator tier). The church *list* stays manager+.
  if (/^\/admin\/churches\/[^/]+/.test(path)) return 'operator';
  let best = null;
  for (const [prefix, tier] of ADMIN_ROUTE_TIERS) {
    if (path.startsWith(prefix) && (!best || prefix.length > best[0].length)) {
      best = [prefix, tier];
    }
  }
  return best ? best[1] : null;
};

export default ADMIN_ROUTE_TIERS;
